import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { scheduleRefresh, clearRefreshTimer } from "../utils/auth";
import { primeCsrf } from "../utils/auth";

export default function Login() {
  const [formData, setFormData] = useState({ email: "", password: "", totp: "" });
  const [needTotp, setNeedTotp] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "totp") {
      const v = value.replace(/\D/g, "").slice(0, 6);
      setFormData((p) => ({ ...p, totp: v }));
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
    }
  };

  const goToVerifyUnauthed = (email) => {
    // nuke any old token, stop timers & headers so interceptors won't refresh
    try {
      clearRefreshTimer();
      localStorage.removeItem("accessToken");
      delete api.defaults?.headers?.common?.Authorization;
    } catch {}
    const qs = email ? `?email=${encodeURIComponent(email.trim())}` : "";
    // hard navigation to avoid guards/interceptors side-effects
    window.location.replace(`/verify${qs}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);

    try {
      await primeCsrf(); // make sure XSRF-TOKEN exists
      const payload = {
        email: formData.email.trim(),
        password: formData.password,
        ...(needTotp && /^[0-9]{6}$/.test(formData.totp) ? { totp: formData.totp } : {}),
      };

      const res = await api.post("/api/auth/login", payload);
      const accessToken = res.data?.accessToken ?? res.data?.token;
      const loginUser   = res.data?.user;

      if (accessToken === "TOTP_REQUIRED") {
        setNeedTotp(true);
        setMessage("Enter your 6-digit two-factor code.");
        return;
      }

      // detect "not verified" in any of the common shapes
      const notVerified =
        accessToken === "EMAIL_NOT_VERIFIED" ||
        res.data?.code === "EMAIL_NOT_VERIFIED" ||
        res.data?.needsEmailVerification === true ||
        loginUser?.emailVerified === false ||
        loginUser?.isEmailVerified === false ||
        ["UNVERIFIED", "PENDING"].includes(loginUser?.verificationStatus);

      if (notVerified) {
        // DO NOT call login() or scheduleRefresh here
        goToVerifyUnauthed(formData.email || loginUser?.email || "");
        return;
      }

      if (!accessToken) {
        setMessage("Login failed.");
        return;
      }

      // Verified → normal login
      login(accessToken);
      scheduleRefresh(accessToken);
      navigate("/");
    } catch (err) {
      if (!err.response) {
        setMessage("Network error. Please try again.");
      } else {
        const raw = err.response.data;
        const text = typeof raw === "string" ? raw : raw?.message || raw?.error || "Login failed";

        if (/verify your email/i.test(text) || /not verified/i.test(text)) {
          goToVerifyUnauthed(formData.email);
          return;
        }
        if (/locked/i.test(text)) {
          setMessage(text);
        } else {
          setMessage(text);
        }
      }
      setFormData((p) => ({ ...p, password: "" }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="mx-auto max-w-lg">
        <section className="card p-6 md:p-8">
          <header className="mb-6 text-center">
            <h1 className="font-display text-3xl md:text-4xl">
              Welcome <span className="bg-gradient-to-r from-brand-500 to-glow bg-clip-text text-transparent">back</span>
            </h1>
            <p className="mt-2 text-slate-300">Log in to manage tickets, submissions & more.</p>
          </header>

          {message && (
            <div className="mb-4 rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-slate-200" aria-live="polite">
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="field">
              <label htmlFor="email" className="label">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                className="input"
                placeholder="you@example.com"
              />
            </div>

            <div className="field">
              <label htmlFor="password" className="label">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                maxLength={72}
                className="input"
                placeholder="••••••••"
              />
            </div>

            {!needTotp ? (
              <button type="button" onClick={() => setNeedTotp(true)} className="btn-ghost text-sm">
                I use two-factor authentication
              </button>
            ) : (
              <div className="field">
                <label htmlFor="totp" className="label">2FA code</label>
                <input
                  id="totp"
                  name="totp"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="6-digit code"
                  value={formData.totp}
                  onChange={handleChange}
                  className="input tracking-widest"
                  autoComplete="one-time-code"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (needTotp && formData.totp.length > 0 && formData.totp.length !== 6)}
              className="btn-primary w-full mt-2"
            >
              {loading ? "Logging in…" : "Log in"}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-sm text-slate-300">
            <Link to="/forgot-password" className="underline">Forgot password?</Link>
            <span>
              New here? <Link to="/register" className="text-slate-200 underline">Create an account</Link>
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}
